import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

interface DeviceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  deviceDetails: any;
}

const DeviceDetailsModal: React.FC<DeviceDetailsModalProps> = ({
  open,
  onClose,
  deviceDetails,
}) => {
  const { t } = useTranslation(["bugherd"]);

  if (!deviceDetails) return null;

  const renderValue = (value: any) => {
    if (typeof value === "boolean") {
      return value ? t("Yes") : t("No");
    }
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getDisplayKey = (key: string) => {
    if (key === "screen") return "Resolution";
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  const renderListItems = (data: any) => {
    const items = [];

    Object.entries(data).forEach(([key, value]) => {
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        // For nested objects, create a section header
        items.push(
          <ListItem key={key} sx={{ px: 0, py: 0.5 }}>
            <ListItemText
              primary={
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, color: "text.secondary" }}
                >
                  {getDisplayKey(key)}
                </Typography>
              }
            />
          </ListItem>
        );

        // Add nested items with the same alignment as main items
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          items.push(
            <ListItem key={`${key}-${nestedKey}`} sx={{ px: 0, py: 0.5 }}>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {getDisplayKey(nestedKey)}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        ml: 1,
                        maxWidth: "60%",
                        wordBreak: "break-all",
                        textAlign: "right",
                      }}
                    >
                      {renderValue(nestedValue)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          );
        });
      } else {
        // For simple key-value pairs
        items.push(
          <ListItem key={key} sx={{ px: 0, py: 0.5 }}>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {getDisplayKey(key)}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      ml: 1,
                      maxWidth: "60%",
                      wordBreak: "break-all",
                      textAlign: "right",
                    }}
                  >
                    {key === "url" ? (
                      <Typography
                        component="a"
                        href={renderValue(value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "text.primary",
                          textDecoration: "none",
                          fontSize: "inherit",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {renderValue(value)}
                      </Typography>
                    ) : (
                      renderValue(value)
                    )}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        );
      }
    });

    return items;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: "400px",
          maxWidth: "500px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 1,
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontSize: "1.1rem" }}>
          {t("System Information")}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ px: 2, py: 1, maxHeight: "400px", overflow: "auto" }}
      >
        <List sx={{ py: 0 }}>{renderListItems(deviceDetails)}</List>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1, borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={onClose} variant="contained" size="small">
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceDetailsModal;
